import { call, put, select, take, takeLatest } from 'redux-saga/effects'
import { revenue, Revenue } from '@amplitude/analytics-browser'
import * as constants from './constants'
import * as actions from './actions'
import { indicateProgress } from 'api'
import { languageSelector } from 'i18n/selectors'
import { getLocationDataBind, getScanticketPromo, getTicketInfo, payForScanticket } from 'grs/api'
import {
    scanticketLocationSelector,
    scanticketTotalPriceSelector,
    scanticketPromoSelector,
    scanticketTicketSelector,
} from 'scanticket/selectors'
import { clearScanticket, redeemScanticketPromoSuccess, scanTicketSuccess } from './actions'
import { userSelector } from 'auth/selectors'
import { getPaymentMethod, GOOGLE_PAY_METHOD, PIX_PAYMENT_METHOD } from 'shared/transactions'
import { extractGooglePayFields, loadGooglePayScript, requestPayment } from 'credit-cards/google-pay/service'
import { getRwgToken, sendGTAGEvent } from 'analytics/utils'
import { ANALYTICS_FLOW_TYPE_SCAN_TICKET } from 'analytics/constants'
import { LOCATION_CHANGE } from 'browser-location'
import { setPixQRCodeData } from 'credit-cards/actions'
import featureToggles from 'shared/featureToggles'
import { getRecaptchaToken } from 'shared/recaptcha'

function* scanTicketConfirmed() {
    while (true) {
        const { payload } = yield take(LOCATION_CHANGE)
        const pathname = payload?.pathname

        if (pathname.includes('/scanticket/confirmation')) return true
        if (pathname.includes('/pix')) continue
        return false
    }
}

function* getGooglePayFieldsForScanTicketPurchase() {
    yield call(loadGooglePayScript)
    const location = yield select(scanticketLocationSelector)
    const totalPrice = yield select(scanticketTotalPriceSelector)
    const paymentData = yield call(requestPayment, location?.merchantID, totalPrice)
    const googlePayFields = extractGooglePayFields(paymentData)

    if (!googlePayFields) {
        console.error('Missing Google Pay token')
        return
    }

    return googlePayFields
}

export function* getScanTicketLocationSaga({ grsId }) {
    try {
        yield indicateProgress('scanTicketLocation', function* () {
            const language = yield select(languageSelector)
            const location = yield call(getLocationDataBind, [grsId], language)

            if (!location || !location.length) {
                throw new Error(`Could not find location for GRs ID: ${grsId}. Please check the GRs ID and try again.`)
            }
            yield put(actions.getScanticketLocationSuccess(location[0]))
        })
    } catch (error) {}
}

function* reedemScanticketPromoSaga({ promoCode }) {
    try {
        yield indicateProgress('reedemScanticketPromo', function* () {
            const language = yield select(languageSelector)
            const location = yield select(scanticketLocationSelector)
            const { ticketNumber } = yield select(scanticketTicketSelector)
            const response = yield call(getScanticketPromo, promoCode, ticketNumber, location.id, language)
            yield put(redeemScanticketPromoSuccess({ ...response, promoCode }))
        })
    } catch {}
}

function* getScanTicketSaga({ ticketNumber }) {
    try {
        yield indicateProgress('getScanTicket', function* () {
            const language = yield select(languageSelector)
            const location = yield select(scanticketLocationSelector)

            const response = yield call(getTicketInfo, ticketNumber, location.id, language)
            yield put(scanTicketSuccess(response))
        })
    } catch {}
}

function* buyScanticketSaga({ paymentMethod, successCallback, personalDataForm }) {
    try {
        yield indicateProgress('buyScanticket', function* () {
            const language = yield select(languageSelector)
            const location = yield select(scanticketLocationSelector)
            const { ticketNumber, amountDue, originalAmount } = yield select(scanticketTicketSelector)
            const promo = yield select(scanticketPromoSelector)
            const user = yield select(userSelector)
            const paymentMethodType = getPaymentMethod(paymentMethod)
            const isPixPayment = paymentMethod?.id === PIX_PAYMENT_METHOD
            const isGooglePayPayment = paymentMethod?.id === GOOGLE_PAY_METHOD

            const payload = {
                TicketNumber: ticketNumber,
                eDataLocationId: location.id,
                IsoLangCode: language,
            }

            if (promo) {
                payload.CouponCode = promo.promoCode
            }

            if (isGooglePayPayment) {
                const googlePayFields = yield call(getGooglePayFieldsForScanTicketPurchase)
                payload.CartPaymentUpdateInfo = {
                    GoogleAPIFields: googlePayFields,
                }
            } else if (isPixPayment) {
                payload.PMTMHD = PIX_PAYMENT_METHOD
            } else {
                payload.CRDNAME = paymentMethod.nickName
            }

            if (!user) {
                payload.PKRFNAME = personalDataForm.firstName
                payload.PKRLNAME = personalDataForm.lastName
                payload.PKRMOB = personalDataForm.phone
                payload.PKREMAIL = personalDataForm.email
                payload.IsGuestToken = true

                if (featureToggles.brNeoPurchaseFlow) {
                    payload.CUSTDOCTYPE = personalDataForm.documentType
                    payload.CUSTDOCNUM = personalDataForm.documentNumber.replace(/\D/g, '')
                }

                if (!isPixPayment) {
                    payload.CCToken = paymentMethod.token
                }
            } else {
                payload.token = user.token
                if (!isPixPayment) {
                    payload.CCID = paymentMethod.id
                }
            }

            const recaptchaToken = yield call(getRecaptchaToken)
            const response = yield call(payForScanticket, payload, recaptchaToken)
            if (response) {
                const fireAnalytics = () => {
                    // In scan ticket we do not get back product ID. We might have to use “Scan ticket” for now
                    const purchase = new Revenue()
                        .setProductId('Scan ticket')
                        .setPrice(amountDue || originalAmount)
                        .setQuantity(1)
                        .setRevenueType('Purchase')

                    purchase.setEventProperties({
                        'Transaction ID': response.orderId,
                        Coupon: promo?.promoCode || '',
                        'Flow Type': ANALYTICS_FLOW_TYPE_SCAN_TICKET,
                        'Payment Method': paymentMethodType,
                        'Google Reserve': Boolean(getRwgToken()),
                    })

                    revenue(purchase)

                    sendGTAGEvent(
                        'purchase',
                        {
                            product_id: 'Scan ticket',
                            value: amountDue || originalAmount,
                            coupon: promo?.promoCode || '',
                            flow_type: ANALYTICS_FLOW_TYPE_SCAN_TICKET,
                            payment_method: paymentMethodType,
                            items: [
                                {
                                    item_id: location?.id,
                                    item_name: location?.name,
                                    coupon: promo?.promoCode || '',
                                    quantity: 1,
                                },
                            ],
                        },
                        user?.id
                    )
                }

                if (response.qRCodeData) {
                    response.qRCodeData.redirectUrl = `/scanticket/confirmation/${response.freeexituntil}`
                    yield put(setPixQRCodeData(response.qRCodeData))

                    try {
                        if (yield call(scanTicketConfirmed)) {
                            fireAnalytics()
                        }
                    } catch (e) {}
                } else {
                    fireAnalytics()
                    successCallback(response)
                }

                yield put(clearScanticket)
            }
        })
    } catch (e) {}
}

export default function* rootSaga() {
    yield takeLatest(constants.GET_SCANTICKET_LOCATION, getScanTicketLocationSaga)
    yield takeLatest(constants.REDEEM_SCANTICKET_PROMO, reedemScanticketPromoSaga)
    yield takeLatest(constants.BUY_SCANTICKET, buyScanticketSaga)
    yield takeLatest(constants.SCAN_TICKET, getScanTicketSaga)
}
